import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, HeaderGroup, Row } from 'react-table'

import Box from '@mui/material/Box'
import Chip, { ChipProps } from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import VisibilityRounded from '@mui/icons-material/VisibilityRounded'
import AddToQueueIcon from '@mui/icons-material/AddToQueue'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'

import colors from '@/assets/scss/_themes-vars.module.scss'
import tableNoData from '@/assets/svg/table-no-data.svg'
import { useIsUS } from '@/hooks/useIsUS'
import { usePatientIdentifierLabel } from '@/hooks/usePatientIdentifierLabel'
import DelayedRender from '@/components/hoc/DelayedRender'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useGetPatientQueueByStaffQuery } from '@/core/services/patient-queue'
import { isPatientInQueue } from '@/features/patient/patient-queue'
import { isPDS } from '@/utils/type-guards'
import { formatBirthday } from '@/utils/dateUtils'
import { useStartLocalConsultation } from '@/shared/model/useStartLocalConsultation'

import { usePatientsTableSelection, PatientData } from '../model/usePatientsTableSelection'
import { usePatientsTableActions } from '../model/usePatientsTableActions'

export type { PatientData }

export type PatientsTableProps = {
  autoHightlight?: boolean
  data: PatientData[]
  disableSelectRow?: boolean
  loading?: boolean
  onSelect?: (data: PatientData) => void
  onOpen?: (data: PatientData, tableState: { disableSelectRow?: boolean }) => void
  selectedIds?: (string | null)[]
  hideActions?: boolean
  showAddToPatientQueueButton?: boolean
  showQueuePatientButton?: boolean
}

const rowHeight = 60

export function PatientsTable(props: PatientsTableProps) {
  const {
    autoHightlight = true,
    data = [],
    disableSelectRow = false,
    loading,
    onSelect,
    onOpen,
    selectedIds,
    hideActions = false,
    showAddToPatientQueueButton = false,
  } = props
  const { t } = useTranslation()
  const isUS = useIsUS()
  const identifierLabel = usePatientIdentifierLabel()

  const authUser = useAppSelector(selectAuthUser)
  const { data: queueData } = useGetPatientQueueByStaffQuery()
  const { startLocalConsultation } = useStartLocalConsultation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('field.lastName.label'),
        accessor: 'surname',
        Cell: ({ value: surname }: CellProps<PatientData, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {surname}
          </Typography>
        ),
      },
      {
        Header: t('field.firstName.label'),
        accessor: 'name',
        Cell: ({ value: name }: CellProps<PatientData, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {name}
          </Typography>
        ),
      },
      {
        Header: t('field.gender.label'),
        accessor: 'gender',
        Cell: ({ value: gender }: CellProps<PatientData, PatientData['gender']>) => {
          if (!gender) return null
          let color: ChipProps['color']
          switch (gender) {
            case 'M':
              color = 'primary'
              break
            case 'F':
              color = 'secondary'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={gender} size="small" variant="filled" />
        },
      },
      {
        Header: t('field.birthday.label'),
        accessor: 'birthday',
        Cell: ({ value: birthday }: CellProps<PatientData, string>) => (
          <Chip color="info" label={formatBirthday(birthday, isUS)} size="small" variant="filled" />
        ),
      },
      {
        Header: t('field.city.label'),
        accessor: 'city',
      },
      {
        Header: identifierLabel,
        accessor: 'secu',
      },
    ] as Column<PatientData>[]
  }, [identifierLabel, isUS, t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<PatientData>({
    columns,
    data,
  })

  const { selectRow, isRowSelected } = usePatientsTableSelection({
    rows,
    autoHightlight,
    disableSelectRow,
    selectedIds,
    onSelect,
  })

  const { addToQueue } = usePatientsTableActions()

  const handleSelect = (row: Row<PatientData>) => () => {
    selectRow(row)
  }

  const handleOpen = (event: React.MouseEvent, row: Row<PatientData>) => {
    event.stopPropagation()
    onOpen?.(row.original, { disableSelectRow })
  }

  return (
    <Table stickyHeader id="patients-table" aria-label={t('patient:table.title', { defaultValue: 'Liste des patients' }) as string}>
      <TableHead sx={{ bgcolor: colors.gray100 }}>
        {headerGroups.map((headerGroup: HeaderGroup<PatientData>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup<PatientData>) => {
              return (
                <TableCell
                  {...column.getHeaderProps()}
                  scope="col"
                  sx={{
                    color: colors.gray600,
                    fontSize: '12px',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {column.render('Header')}
                </TableCell>
              )
            })}
            {!hideActions && <TableCell width="100px" align="center"></TableCell>}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
        {loading && (
          <DelayedRender delay={300}>
            <TableRow>
              <TableCell sx={{ border: 'none', padding: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              </TableCell>
            </TableRow>
          </DelayedRender>
        )}
        {(!data.length || !rows.length) && (
          <TableRow>
            <TableCell colSpan={columns.length + (!hideActions ? 1 : 0)}>
              <Stack alignItems="center" justifyContent="center" p="80px 24px">
                <img alt="no-data" src={tableNoData} />
              </Stack>
            </TableCell>
          </TableRow>
        )}
        {rows.map((row, index) => {
          prepareRow(row)
          return (
            <TableRow
              {...row.getRowProps()}
              key={row.original._id}
              onClick={handleSelect(row)}
              selected={isRowSelected(row.original._id)}
              sx={[!disableSelectRow && { cursor: 'pointer' }]}
              id={`patient-row-${index}`}
            >
              {row.cells.map((cell: Cell<PatientData>) => (
                <TableCell {...cell.getCellProps()} align="left" sx={{ height: rowHeight + 'px', verticalAlign: 'middle' }}>
                  {cell.render('Cell')}
                </TableCell>
              ))}

              {!hideActions && (
                <TableCell key={`cell_${row.index}_actions`} align="center" sx={{ height: rowHeight + 'px', verticalAlign: 'middle' }}>
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <Tooltip title={t('page.patients.seeProfileButton.tooltip')}>
                      <IconButton onClick={(event) => handleOpen(event, row)} data-testid="see-profile-button">
                        <VisibilityRounded htmlColor="#269CA6" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('page.infoPatient.localConsultationButton.label')}>
                      <IconButton onClick={() => startLocalConsultation(row.original._id)} data-testid="start-local-consultation-button">
                        <LocalHospitalIcon htmlColor="#269CA6" />
                      </IconButton>
                    </Tooltip>
                    {showAddToPatientQueueButton && isPDS(authUser) && (
                      <Tooltip
                        title={
                          queueData && isPatientInQueue(row.original._id, queueData) ? t('Patient already in queue') : t('page.patients.addToQueueButton.tooltip')
                        }
                      >
                        <IconButton onClick={() => addToQueue(row.original._id)} disabled={queueData && isPatientInQueue(row.original._id, queueData)}>
                          <AddToQueueIcon htmlColor={queueData && isPatientInQueue(row.original._id, queueData) ? '#ccc' : '#269CA6'} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default PatientsTable
